// src/index.js
// W5: die zwei Fensterlauscher zuerst, sie muessen haengen, bevor irgendein
// anderer Import ausgewertet wird (Grill W6). Fehler davor bleiben unsichtbar.
import './utils/fehlerlauscher';
import React from 'react';
import ReactDOM from 'react-dom/client';
import axios from 'axios';
import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import { SessionContextProvider } from '@supabase/auth-helpers-react';
import { NotificationProvider } from './notifications/NotificationProvider';
import reportWebVitals from './reportWebVitals';
import * as serviceWorkerRegistration from './serviceWorkerRegistration';
import App from './App';
import './index.css';
import './theme/variables.css';
import {
  supabase,
  setAxiosAuthDefault,
  getFreshAccessToken,
  refreshSessionOnce,
  isPublicUrl,
  isCrossOriginUrl,
} from './api/authHelpers';
// UI2 Teil 9, Z-1a: der geteilte Erreichbarkeits-Waechter. Die zwei Haken HIER
// (globales axios + `window.fetch`) plus der in `./api/client` decken jeden der
// drei Netzwerkwege; ein vierter Weg gibt es nicht (§1.1 des Plans).
import { merkeFehlschlag, merkeErfolg, setzeOriginalFetch } from './api/serviceOutage';

const emotionCache = createCache({ key: 'css', prepend: true });

// ─── Auth token plumbing ────────────────────────────────────────────────────
//
// The backend's global JWT gate (backend/global_auth_gate.py) 401s any /api/*
// request without a valid Authorization: Bearer <jwt>. Three failure modes
// were observed in prod logs (2026-04-30 → 2026-05-01): (1) cached access
// token expired but autoRefreshToken background timer didn't fire because tab
// was hidden / laptop sleeping, (2) burst of parallel API calls all sending
// the same expired token in lock-step, (3) backend 401 returned no recovery
// affordance — UI silently broke until full reload.
//
// Helpers (supabase, getFreshAccessToken, refreshSessionOnce, etc.) live in
// `./api/authHelpers` so the shared `api` instance in `./api/client` reuses
// the same single-flight refresh + URL guards. Default-axios + window.fetch
// wrappers below stay here — they intercept browser globals, not module-
// scoped axios instances.

const _origFetch = window.fetch.bind(window);

// Z-1a: die Sonde des Waechters faehrt ueber die UNVERPACKTE Referenz (sonst
// wartete sie erst auf den Token-Abruf und liefe mit ihrem eigenen Fehlschlag
// erneut in den Zaehler — Rueckkopplung). Ueber den Setter, NICHT ueber einen
// Import: `_origFetch` wird erst hier definiert, ein Import liefe in die TDZ.
setzeOriginalFetch(_origFetch);

// Seed axios defaults header on startup + react to auth-state changes so
// axios.create() instances always have the current token. Kept until all 11
// callsites migrate to import { api } from './api/client' (Phase 3 cleanup).
supabase.auth.getSession().then(({ data }) => {
  setAxiosAuthDefault(data?.session?.access_token || null);
}).catch(() => { /* no session yet — header stays unset */ });

supabase.auth.onAuthStateChange((_event, session) => {
  setAxiosAuthDefault(session?.access_token || null);
});

// ─── axios: request interceptor (inject) + response interceptor (401 retry) ──
axios.interceptors.request.use(async (config) => {
  const url = config.url || '';
  // Public / cross-origin URLs must NEVER carry the Bearer JWT — strip any
  // value the defaults header may have seeded (setAxiosAuthDefault writes to
  // axios.defaults.headers.common.Authorization, which gets merged into config
  // before this interceptor runs).
  if (isPublicUrl(url) || isCrossOriginUrl(url)) {
    if (config.headers && config.headers.Authorization) {
      delete config.headers.Authorization;
    }
    return config;
  }
  try {
    const token = await getFreshAccessToken();
    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }
  } catch { /* unauthed; backend will 401 + interceptor retries */ }
  return config;
});

axios.interceptors.response.use(
  (response) => {
    merkeErfolg(response?.config?.url);
    return response;
  },
  async (error) => {
    const config = error?.config;
    const status = error?.response?.status;
    // Z-1a: VOR dem 401-Zweig zaehlen. Ein 401 ist kein Ausfall (der Zaehler
    // verwirft ihn); ein Netzfehler hat `status == null` und zaehlt als solcher.
    // 🔴 Der Fehler geht MIT — sonst zaehlt ein `CanceledError` (Abbruch ohne
    // Antwort, `status == null`) als Netzfehler. Genau das war der Befund der
    // Nachbesserung 3: die RMA-Haken brechen ihren Aufruf im Aufraeumer ab
    // (`useCaseLive.js`), und der Waechter fuhr eine Sonde gegen einen Dienst,
    // der die ganze Zeit erreichbar war.
    merkeFehlschlag(status, config?.url, error);
    if (status !== 401 || !config || config._pogRetry) {
      return Promise.reject(error);
    }
    const url = config.url || '';
    if (isPublicUrl(url)) return Promise.reject(error);
    const refreshed = await refreshSessionOnce();
    if (!refreshed) return Promise.reject(error);
    config._pogRetry = true;
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${refreshed.access_token}`;
    return axios(config);
  },
);

// ─── window.fetch wrapper: same logic for raw fetch callsites ───────────────
// Z-1a zaehlt an DIESER Stelle die rohen `fetch`-Aufrufe (61 in 19 Dateien).
// Erfolg UND Fehlschlag gehen durch `zaehleAntwort`; ein Netzfehler braucht ein
// eigenes try/catch, weil `fetch` ABWEIST und die Ablehnung sonst am Zaehler
// vorbeiliefe (W3).
const zaehleAntwort = (antwort, url) => {
  if (!antwort) return;
  if (antwort.ok) merkeErfolg(url);
  else merkeFehlschlag(antwort.status, url);
};

window.fetch = async (input, init) => {
  const url = typeof input === 'string' ? input : input?.url || '';

  // Cross-origin: reiner Ausstieg OHNE Zaehlung — ein Fremdhost sagt nichts
  // darueber, ob Forge erreichbar ist.
  if (isCrossOriginUrl(url)) {
    return _origFetch(input, init);
  }

  // Oeffentliche /api/-Pfade (E-5): weiterhin OHNE Authorization-Kopf, aber die
  // Antwort wird GEZAEHLT. Ueber diesen Zweig meldet `/vendor-reports/public/…`
  // den Ausfall — die Seite holt genau so ihre Daten.
  if (isPublicUrl(url)) {
    try {
      const antwort = await _origFetch(input, init);
      zaehleAntwort(antwort, url);
      return antwort;
    } catch (fehler) {
      // Der Fehler geht MIT: ein `AbortError` (fetch mit `signal`) ist kein Ausfall.
      merkeFehlschlag(null, url, fehler);
      throw fehler;
    }
  }

  const buildInit = (token) => {
    const next = init ? { ...init } : {};
    const headers = new Headers(next.headers || {});
    if (token) headers.set('Authorization', `Bearer ${token}`);
    next.headers = headers;
    return next;
  };

  let token = null;
  try { token = await getFreshAccessToken(); } catch { /* go unauthed */ }

  let response;
  try {
    response = await _origFetch(input, buildInit(token));
  } catch (fehler) {
    // W3: die Zusage wird ABGEWIESEN — zaehlen und weiterwerfen, damit das
    // Verhalten der 61 Aufrufer unveraendert bleibt.
    // 🔴 Der Fehler geht MIT: ein `AbortError` (fetch mit `signal`, z. B. der
    // Abbruch im Aufraeumer eines Effekts) ist kein Ausfall.
    merkeFehlschlag(null, url, fehler);
    throw fehler;
  }
  if (response.status !== 401) {
    zaehleAntwort(response, url);
    return response;
  }

  // Retry once with a freshly refreshed token.
  const refreshed = await refreshSessionOnce();
  if (!refreshed) return response;
  let retry;
  try {
    retry = await _origFetch(input, buildInit(refreshed.access_token));
  } catch (fehler) {
    // 🔴 R16-W6 (Nachbesserung 4, 2026-10-11): der WIEDERHOLAUFRUF ist ein
    // Netzwerkweg wie jeder andere. Bis hierher sass nur der erste `_origFetch`
    // in einem `try/catch`; brach der Wiederholaufruf ab, lief sein Fehlschlag
    // am Zaehler VORBEI — die Aussage „jeder Netzwerkweg zaehlt" war zu stark.
    // Der Abbruch bleibt ausgenommen (`istAbbruch` in `merkeFehlschlag`), und
    // die Ablehnung geht unveraendert weiter (Verhalten der 61 Aufrufer).
    merkeFehlschlag(null, url, fehler);
    throw fehler;
  }
  zaehleAntwort(retry, url);
  return retry;
};

// ─── Foreground refresh: when tab becomes visible, ensure token is fresh ────
// Catches the laptop-sleep case: autoRefreshToken pauses while tab hidden,
// so on wake the cached access token is stale. Pre-emptively refreshing
// avoids the first-click 401 cascade Samuel reported.
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible') {
    // Fire and forget — getFreshAccessToken will refresh if near expiry.
    getFreshAccessToken().catch(() => { /* ignore — UI will recover via 401 retry */ });
  }
});

const container = document.getElementById('root');
const root = ReactDOM.createRoot(container);

root.render(
  <React.StrictMode>
    <CacheProvider value={emotionCache}>
      <SessionContextProvider supabaseClient={supabase}>
        <NotificationProvider>
          <App />
        </NotificationProvider>
      </SessionContextProvider>
    </CacheProvider>
  </React.StrictMode>
);

reportWebVitals();

// Register service worker for offline-first app shell caching.
// Data/API requests are never cached — only the static shell.
serviceWorkerRegistration.register();
